import { useEffect, useLayoutEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import Layout from '@/components/layout/Layout'
import WikiPage from '@/pages/WikiPage'
import SearchPage from '@/pages/SearchPage'
import EcosystemNotFound from '@/components/common/EcosystemNotFound'
import { getPages } from '@/api/wiki'
import usePageTracking from '@/hooks/usePageTracking'

const rawBase = import.meta.env.BASE_URL || '/'
const basename = rawBase === '/' ? undefined : rawBase.replace(/\/$/, '')

function LegacyWikiRedirect() {
  const { slug } = useParams<{ slug: string }>()
  if (!slug) return <Navigate to="/" replace />
  return <Navigate to={`/${slug}`} replace />
}

function HomeRedirect() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function run() {
      try {
        const pages = await getPages()
        if (cancelled) return

        if (pages.length > 0) {
          // Default landing page should be the "About Calamity" page (first in the sidebar).
          // If it doesn't exist (or gets renamed), fall back to the first returned page.
          const preferred = pages.find((page) => page.slug === 'about-calamity')
          navigate(`/${(preferred ?? pages[0]).slug}`, { replace: true })
          return
        }

        setError(true)
      } catch {
        if (cancelled) return
        setError(true)
      } finally {
        if (cancelled) return
        setLoading(false)
      }
    }

    run()
    return () => { cancelled = true }
  }, [navigate])

  if (loading) {
    return (
      <div className="panel p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-calamity-panel-hover rounded-lg w-2/3" />
          <div className="h-4 bg-calamity-panel-hover rounded w-3/4" />
          <div className="h-4 bg-calamity-panel-hover rounded w-1/2" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="panel p-8 text-center">
        <div className="text-5xl mb-4 opacity-50">&#128214;</div>
        <h1 className="text-2xl font-display text-calamity-text mb-2">No Pages Yet</h1>
        <p className="text-calamity-text-dim">
          No published wiki pages were found.
        </p>
      </div>
    )
  }

  return null
}

function ScrollToTop() {
  const { pathname, hash } = useLocation()

  // useLayoutEffect avoids a one-frame "flash" at the previous scroll position on navigation.
  useLayoutEffect(() => {
    // Keep anchor navigation intact.
    if (hash) return
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname, hash])

  return null
}

function AppRoutes() {
  usePageTracking()

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomeRedirect />} />
          {/* Legacy URLs from older router config */}
          <Route path="wiki/:slug" element={<LegacyWikiRedirect />} />
          {/* Canonical wiki routes */}
          <Route path=":slug" element={<WikiPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="*" element={<EcosystemNotFound app="wiki" />} />
        </Route>
      </Routes>
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter basename={basename}>
      <AppRoutes />
    </BrowserRouter>
  )
}
